import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, useNavigate, useLocation } from "react-router-dom";
import { app, pages } from '@microsoft/teams-js'; // Using the imported 'app' and HostClientType for context check

import { FluentProvider, webLightTheme, webDarkTheme, teamsHighContrastTheme } from "@fluentui/react-components"; // Removed Button as it's not used here

import { Theme } from "@radix-ui/themes";
import toast, { Toaster } from "react-hot-toast"; // toast is default export
import ProtectedRoutes from "./ProtectedRoutes";
import PublicRoutes from "./PublicRoutes";
import DataProvider from "./context/DataProvider";
import WebDataProvider from "./context/WebDataProvider";
import BotsDataProvider from "./context/BotsDataProvider";
import { WebSocketProvider } from './context/WebSocketProvider';
import "./App.css";
import { BotSessionsProvider } from "./context/BotSessionsProvider";
import { WorkflowExecutionsProvider } from "./context/WorkflowExecutionsProvider";
import { EmailsProvider } from "./context/EmailsProvider";
import { ReactFlowProvider } from "reactflow";
import { useTeams } from "./context/TeamsProvider";
import { McpProvider } from "./context/McpProvider";
import { AgentProvider } from "./context/AgentProvider";
import { TaskPlanProvider } from "./context/TaskPlanProvider";
import { NotificationProvider } from "./context/NotificationProvider";

// Your App ID from the manifest
const TEAMS_APP_ID = process.env.REACT_APP_TEAMS_APP_ID

const AppContent = () => { // Renamed App to AppContent to use hooks inside Router context
  const [teamsTheme, setTeamsTheme] = useState(webLightTheme);
  const { isTeamsInitialized, setIsTeamsInitialized } = useTeams();

  const [userIdToken, setUserIdToken] = useState(() => localStorage.getItem("userToken"));
  const [projectIdToken, setProjectIdToken] = useState(() => localStorage.getItem("projectToken"));
  const navigate = useNavigate();
  const location = useLocation();

  const onLogin = (user_id_token, received_user_id, project_id_token = null) => { // Assuming onLogin might receive user_id too
    setUserIdToken(user_id_token);
    localStorage.setItem("userToken", user_id_token);
    // If user_id is also important for app state beyond JWT, handle it here
    // navigate("/projects");
    // --- NEW LOGIC: Check for project_token to determine navigation ---
    if (project_id_token) {
      // This is a new signup. Set the project token and navigate directly to the chat page.
      setProjectIdToken(project_id_token);
      localStorage.setItem("projectToken", project_id_token);
    }

    const targetPageId = project_id_token ? "chat" : "projects";
    const targetEndpoint = project_id_token ? "/chat" : "/projects";

    if (isTeamsInitialized) {
      // Attempt to get current context to see if we're already on the target tab
      app.getContext().then(context => {
        const currentTabEntityId = context.page.id;
        if (currentTabEntityId === targetPageId) {
          // Already on the target tab, force a refresh
          console.log(`Already on tab ${targetPageId}. Forcing a refresh (window.location.reload()).`);
          // window.location.reload();
          navigate(targetEndpoint);
        } else {
          // Not on the target tab, proceed with navigation
          console.log(`Navigating to different tab: ${targetPageId} using navigateToApp.`);
          pages.navigateToApp({
            appId: TEAMS_APP_ID,
            pageId: targetPageId,
          }).then(() => {
            console.log(`Successfully navigated to pageId: ${targetPageId} using navigateToApp`);
          }).catch((error) => {
            console.error(`Error navigating to pageId: ${targetPageId} using navigateToApp:`, error);
          });
        }
      }).catch(error => {
        // If getContext fails, fallback to just trying to navigate
        console.error("Error getting Teams context for onLogin refresh logic, falling back to navigateToApp:", error);
        pages.navigateToApp({
          appId: TEAMS_APP_ID,
          pageId: targetPageId,
        }).then(() => {
          console.log(`Successfully navigated to pageId: ${targetPageId} using navigateToApp (getContext fallback)`);
        }).catch((navError) => {
          console.error(`Error navigating to pageId: ${targetPageId} using navigateToApp (getContext fallback):`, navError);
        });
      });
    } else {
      console.log("Non-Teams environment: Navigating to /projects using react-router.");
      // Optional: If already on /projects in browser, force re-render or use other state logic
      if (location.pathname === targetEndpoint) {
        console.log("Already on /projects in non-Teams. Forcing re-evaluation for router.");
        navigate(targetEndpoint, { replace: true, state: { timestamp: Date.now() } }); // Helps trigger re-render in some cases
      } else {
        navigate(targetEndpoint);
      }
    }
  };

  const onProjectAccess = (project_id_token) => {
    setProjectIdToken(project_id_token);
    localStorage.setItem("projectToken", project_id_token);
    // navigate("/chat");
    const targetPageId = "chat";
    // const targetSubPageId = "generalChannel"; // Optional

    if (isTeamsInitialized) {
      console.log(`Attempting Teams navigation to pageId: ${targetPageId}`);
      if (pages.currentApp.isSupported()) {
        pages.currentApp.navigateTo({
          pageId: targetPageId,
          // subPageId: targetSubPageId,
        }).then(() => {
          console.log(`Successfully navigated to pageId: ${targetPageId} using currentApp.navigateTo`);
        }).catch((error) => {
          console.error(`Error navigating to pageId: ${targetPageId} using currentApp.navigateTo:`, error);
        });
      } else {
        console.warn("pages.currentApp.navigateTo is not supported, attempting fallback with pages.navigateToApp");
        pages.navigateToApp({
          appId: TEAMS_APP_ID,
          pageId: targetPageId,
          // subPageId: targetSubPageId,
        }).then(() => {
          console.log(`Successfully navigated to pageId: ${targetPageId} using navigateToApp`);
        }).catch((error) => {
          console.error(`Error navigating to pageId: ${targetPageId} using navigateToApp:`, error);
          // navigate(`/${targetPageId}`);
        });
      }
    } else {
      console.log("Non-Teams environment: Navigating to /chat using react-router.");
      navigate("/chat");
    }
  };

  const handleStorageChange = () => { // This might be for cross-tab sync, ensure it's used if needed
    setUserIdToken(localStorage.getItem("userToken"));
    setProjectIdToken(localStorage.getItem("projectToken"));
  };

  useEffect(() => {
    // console.log("App.js: Attempting to initialize Teams SDK using imported 'app' module...");

    // Directly use the imported 'app' object.
    // The initialize() promise will resolve if in Teams, and reject/timeout otherwise.
    app.initialize().then(() => {
      // console.log("App.js: Teams SDK Initialized successfully via imported 'app'.");
      setIsTeamsInitialized(true);

      // Notify Teams that your app's main content has loaded.
      app.notifyAppLoaded(); // Use the imported 'app'
      // console.log("App.js: Notified Teams that the app has loaded.");

      app.getContext().then((context) => { // Use the imported 'app'
        // console.log("App.js: Teams Context:", context);
        if (context && context.theme) {
          switch (context.theme) {
            case "dark": setTeamsTheme(webDarkTheme); break;
            case "contrast": setTeamsTheme(teamsHighContrastTheme); break;
            default: setTeamsTheme(webLightTheme); break;
          }
        } else {
          // console.warn("App.js: Teams context or context.theme is undefined.");
          setTeamsTheme(webLightTheme);
        }
      }).catch(error => {
        console.error("App.js: Error getting Teams context:", error);
      });

      app.registerOnThemeChangeHandler((theme) => { // Use the imported 'app'
        // console.log("App.js: Teams theme changed to:", theme);
        switch (theme) {
          case "dark": setTeamsTheme(webDarkTheme); break;
          case "contrast": setTeamsTheme(teamsHighContrastTheme); break;
          default: setTeamsTheme(webLightTheme); break;
        }
      });

    }).catch(error => {
      console.warn("App.js: Teams SDK Initialization failed. Assuming non-Teams context. Error:", error);
      setIsTeamsInitialized(false);
    });
  }, []); // Empty dependency array

  // Optional: Loading state while Teams SDK initializes, if you want to prevent rendering LoginDemo too soon.
  //   if (window.microsoftTeams && !isTeamsInitialized) { // Only show loading if trying to init Teams
  //    return <div>Initializing Teams App integration...</div>;
  //   }

  return (
    <FluentProvider theme={teamsTheme}>
      <Theme>
        <Toaster
          position="top-right"
          toastOptions={{
            success: {
              // icon: "✅",
              style: {
                background: "#18181b",
                color: "#d4d4d8",
                border: "1px solid #27272a",
                borderRadius: "0.5rem",
                padding: "1rem",
              },
            },
            error: {
              icon: "❌",
              style: {
                background: "#18181b",
                color: "#d4d4d8",
                border: "1px solid #27272a",
                borderRadius: "0.5rem",
                padding: "1rem",
              },
            },
          }}
        />
        {(userIdToken && projectIdToken) ? (
          <WebSocketProvider userGoogleId={projectIdToken} >
            <DataProvider key={`${projectIdToken}-${userIdToken}`}>
              <WebDataProvider>
                <BotsDataProvider>
                  <BotSessionsProvider>
                    <WorkflowExecutionsProvider>
                      <ReactFlowProvider>
                        <EmailsProvider >
                          <McpProvider>
                            <AgentProvider>
                              <TaskPlanProvider>
                                <NotificationProvider>
                                  <ProtectedRoutes
                                    userIdToken={userIdToken}
                                    projectIdToken={projectIdToken}
                                    onLogin={onLogin}
                                    onProjectAccess={onProjectAccess}
                                    handleStorageChange={handleStorageChange}
                                    isTeamsInitialized={isTeamsInitialized}
                                  />
                                </NotificationProvider>
                              </TaskPlanProvider>
                            </AgentProvider>
                          </McpProvider>
                        </EmailsProvider>
                      </ReactFlowProvider>
                    </WorkflowExecutionsProvider>
                  </BotSessionsProvider>
                </BotsDataProvider>
              </WebDataProvider>
            </DataProvider>
          </WebSocketProvider>
        ) : (
          <PublicRoutes
            key={`${projectIdToken}-${userIdToken}`}
            userIdToken={userIdToken}
            projectIdToken={projectIdToken}
            onLogin={onLogin}
            onProjectAccess={onProjectAccess}
            handleStorageChange={handleStorageChange}
            isTeamsInitialized={isTeamsInitialized}
          />
        )}
      </Theme>
    </FluentProvider>
  );
};

const App = () => {
  return (
    <AppContent />
  );
};

export default App;